/**
 * 検査ポリシーの編集(Modal + antd Form)。patterns は行追加。regex の可否は画面で判定せず、
 * サーバーの 400 をそのままフォームのエラーに出す(docs/dlp.md §6)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, message } from "antd";
import { useEffect } from "react";

import type { DlpPolicy, DlpPolicyInput } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { KNOWN_EXTENSIONS, KNOWN_ROOTS } from "./constants";
import { errorText, useInvalidateDlp } from "./hooks";

/** 拡張子は小文字・ドット無しで保存する(契約)。入力の揺れをここで吸収する。 */
function normalizeExtensions(values: string[]): string[] {
  const out = values.map((v) => v.trim().toLowerCase().replace(/^\.+/, "")).filter(Boolean);
  return Array.from(new Set(out));
}

const EMPTY: DlpPolicyInput = {
  name: "",
  description: "",
  patterns: [{ name: "", regex: "" }],
  roots: ["Desktop", "Documents", "Downloads"],
  extensions: ["txt", "csv", "md", "docx", "xlsx", "pptx"],
  max_file_mb: 20,
  max_files: 200000,
  time_budget_min: 30,
  is_default: false,
};

export function PolicyForm({
  open,
  policy,
  readOnly,
  onClose,
}: {
  open: boolean;
  /** null なら新規 */
  policy: DlpPolicy | null;
  /** admin でなければ見るだけ */
  readOnly: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateDlp();
  const [form] = Form.useForm<DlpPolicyInput>();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(
      policy
        ? {
            name: policy.name,
            description: policy.description,
            patterns: policy.patterns.length ? policy.patterns : [{ name: "", regex: "" }],
            roots: policy.roots,
            extensions: policy.extensions,
            max_file_mb: policy.max_file_mb,
            max_files: policy.max_files,
            time_budget_min: policy.time_budget_min,
            is_default: policy.is_default,
          }
        : EMPTY,
    );
  }, [open, policy, form]);

  const save = useMutation({
    mutationFn: (v: DlpPolicyInput) =>
      api.dlpSavePolicy(policy?.id ?? null, {
        ...v,
        name: v.name.trim(),
        description: (v.description ?? "").trim(),
        patterns: v.patterns.map((p) => ({ name: p.name.trim(), regex: p.regex })),
        roots: v.roots.map((r) => r.trim()).filter(Boolean),
        extensions: normalizeExtensions(v.extensions),
      }),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.dlpDeletePolicy(policy!.id),
    onSuccess: () => {
      void message.success("削除した(監査ログに記録した)");
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const busy = save.isPending || remove.isPending;
  return (
    <Modal
      open={open}
      width={720}
      title={policy ? (readOnly ? `検査ポリシー: ${policy.name}` : `検査ポリシーを編集: ${policy.name}`) : "検査ポリシーを追加"}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <span>
            {!readOnly && policy && (
              <Popconfirm
                title="このポリシーを削除する"
                description={policy.is_default ? "既定のポリシーは削除できない(別の行を既定にしてから)" : "使ったスキャンの結果は残る(ポリシーの参照だけ外れる)。"}
                okText="削除"
                cancelText="やめる"
                okButtonProps={{ danger: true, disabled: policy.is_default }}
                onConfirm={() => remove.mutate()}
              >
                <Button danger disabled={busy || policy.is_default}>
                  削除
                </Button>
              </Popconfirm>
            )}
          </span>
          <Space>
            <Button onClick={onClose}>{readOnly ? "閉じる" : "やめる"}</Button>
            {!readOnly && (
              <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
                保存
              </Button>
            )}
          </Space>
        </Space>
      }
    >
      {save.isError && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="保存できなかった" description={errorText(save.error)} />
      )}
      <Form<DlpPolicyInput> form={form} layout="vertical" onFinish={(v) => save.mutate(v)} disabled={readOnly || busy}>
        <Form.Item name="name" label="名前" rules={[{ required: true, whitespace: true, message: "名前を入れる" }]}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="description" label="説明">
          <Input maxLength={500} />
        </Form.Item>
        <Form.Item
          name="is_default"
          label="既定"
          valuePropName="checked"
          extra="退職イベントの自動スキャンと、依頼時に何も選ばなかったときに使う。立てると他の行の既定は外れる"
        >
          <Switch />
        </Form.Item>

        <Form.List
          name="patterns"
          rules={[
            {
              validator: (_, v: unknown[]) => (v && v.length ? Promise.resolve() : Promise.reject(new Error("パターンを 1 つ以上"))),
            },
          ]}
        >
          {(fields, { add, remove: removeAt }, { errors }) => (
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>探す語(正規表現。RE2 で動くもの)</div>
              {fields.map((f) => (
                <Space key={f.key} align="start" style={{ display: "flex", marginBottom: 4 }}>
                  <Form.Item name={[f.name, "name"]} rules={[{ required: true, whitespace: true, message: "名前" }]} style={{ marginBottom: 4 }}>
                    <Input placeholder="名前(検出に出る)" style={{ width: 160 }} maxLength={50} />
                  </Form.Item>
                  <Form.Item
                    name={[f.name, "regex"]}
                    style={{ marginBottom: 4 }}
                    // 式の可否はブラウザーで判定しない。JS の RegExp と Go の RE2 は受ける構文が違い
                    // (`(?i)` を JS は拒み、`x{1001}` を RE2 は拒む)、保存を誤って止めたり通したりする。
                    // 最終判定はサーバーの 400 で、上の「保存できなかった」に理由が出る(レビュー #15)
                    rules={[{ required: true, message: "正規表現を入れる" }]}
                  >
                    <Input placeholder="社外秘|部外秘|CONFIDENTIAL" style={{ width: 380 }} maxLength={500} className="ui-num" />
                  </Form.Item>
                  {!readOnly && (
                    <Button type="text" danger onClick={() => removeAt(f.name)} disabled={fields.length <= 1}>
                      削除
                    </Button>
                  )}
                </Space>
              ))}
              <Form.ErrorList errors={errors} />
              {!readOnly && (
                <Button size="small" onClick={() => add({ name: "", regex: "" })}>
                  行を追加
                </Button>
              )}
            </div>
          )}
        </Form.List>

        <Form.Item
          name="roots"
          label="探す場所"
          rules={[{ required: true, message: "1 つ以上" }]}
          extra="相対(Desktop など)は全ユーザープロファイルの C:\\Users\\<name>\\<場所> に展開。絶対パス(C:\\Share)はそのまま"
        >
          <Select mode="tags" options={KNOWN_ROOTS.map((r) => ({ value: r, label: r }))} tokenSeparators={[",", " "]} />
        </Form.Item>
        <Form.Item
          name="extensions"
          label="読む拡張子"
          rules={[{ required: true, message: "1 つ以上" }]}
          normalize={(v: string[]) => normalizeExtensions(v ?? [])}
          extra="小文字・ドット無し。docx / xlsx / pptx は zip 内の XML を読む。PDF は読まない(v0 の制約)"
        >
          <Select mode="tags" options={KNOWN_EXTENSIONS.map((e) => ({ value: e, label: e }))} tokenSeparators={[",", " ", "."]} />
        </Form.Item>
        <Space size={16} wrap>
          <Form.Item name="max_file_mb" label="1 ファイルの上限 (MB)" rules={[{ required: true }]}>
            <InputNumber min={1} max={1024} />
          </Form.Item>
          <Form.Item name="max_files" label="ファイル数の上限" rules={[{ required: true }]}>
            <InputNumber min={1} max={10_000_000} step={1000} />
          </Form.Item>
          <Form.Item name="time_budget_min" label="時間の上限 (分)" rules={[{ required: true }]}>
            <InputNumber min={1} max={1440} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
